import { Button, Flex, HStack } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { AdminPage } from "src/components/Layout/AdminPage";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { SearchAndFilterTypes } from "src/constants";
import {
  deletePaymentTemplate,
  listPaymentTemplates,
  type ListPaymentTemplatesData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { usePaymentConfig } from "src/hooks/usePaymentConfig";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { CreatePaymentDrawer } from "./CreatePaymentDrawer";
import { EditPaymentDrawer } from "./EditPaymentDrawer";
import { Empty } from "./Empty";
import { PaymentsList } from "./List";
import { PaymentNotConfigured } from "./PaymentNotConfigured";
import { RequestPaymentDrawer } from "./RequestPaymentDrawer";
import { Payment } from "./types";

export const Payments = () => {
  const organization = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderBy, setOrderBy } = useOrderByParams();
  const { pagination, setPagination } = usePaginationParams();
  const searchTerm = searchParams.get(SearchAndFilterTypes.Search) ?? "";
  const queryClient = useQueryClient();
  const toast = useAvelaToast();
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({
    header: "Delete payment",
    body: "Are you sure you want to delete this payment? You can't undo this action afterwards.",
  });
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [requestPaymentForId, setRequestPaymentForId] = useState<string | null>(
    null
  );

  const organizationId = organization.map((org) => org.id).withDefault("");

  const templatesRD = useRemoteDataRestQuery({
    queryKey: [
      "paymentTemplates",
      organizationId,
      pagination.limit,
      pagination.offset,
      searchTerm,
      orderBy?.sortKey,
      orderBy?.sortType,
    ],
    queryFn: () =>
      listPaymentTemplates({
        query: {
          organizationId,
          limit: pagination.limit,
          offset: pagination.offset,
          search: searchTerm || undefined,
          sortKey:
            orderBy?.sortKey as ListPaymentTemplatesData["query"]["sortKey"],
          sortType:
            orderBy?.sortType as ListPaymentTemplatesData["query"]["sortType"],
        },
      }).then((response) => response.data?.data),
    enabled: organization.hasData(),
  });

  const handleActionSearch = useCallback(
    (searchTerm: string) => {
      if (!searchTerm) {
        searchParams.delete(SearchAndFilterTypes.Search);
      } else {
        searchParams.set(SearchAndFilterTypes.Search, searchTerm);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearSearch = useCallback(
    () => handleActionSearch(""),
    [handleActionSearch]
  );

  const handleDelete = useCallback(
    async (paymentId: string) => {
      if (!(await confirm())) return;

      try {
        await deletePaymentTemplate({ path: { templateId: paymentId } });
        queryClient.invalidateQueries({ queryKey: ["paymentTemplates"] });
      } catch (error) {
        console.error("Failed to delete payment:", error);
        toast({
          title: "Error",
          description: "Failed to delete payment",
          status: "error",
        });
      }
    },
    [confirm, queryClient, toast]
  );

  const handleRequestPayment = useCallback((paymentId: string) => {
    setRequestPaymentForId(paymentId);
  }, []);

  const hasFilter = useMemo(() => {
    return isNonEmptyString(searchParams.get(SearchAndFilterTypes.Search));
  }, [searchParams]);

  const paymentConfigRD = usePaymentConfig();

  return (
    <RemoteDataView
      remoteData={RD.toTuple3(organization, templatesRD, paymentConfigRD)}
      notAsked={<Loading />}
      loading={<Loading />}
      error={() => <GenericError />}
      reloading={null}
    >
      {([org, page, paymentConfig]) => {
        const payments: Payment[] = page.paymentTemplates.map((template) => ({
          id: template.id,
          name: template.name,
          description: template.description ?? "",
          amount: template.amountInCents / 100,
          inUseCount: template.inUseCount,
        }));
        const totalCount = page.total;
        const showEmptyState = totalCount === 0 && !hasFilter;

        return (
          <AdminPage
            breadcrumb={
              <Breadcrumb
                items={breadcrumb.payments.getBreadcrumbsForList(organization)}
              />
            }
            heading={
              <>
                {!paymentConfig.isPaymentConfigured ? (
                  <PaymentNotConfigured />
                ) : null}
                <AdminHeading
                  title="Payments"
                  actionButton={
                    <HStack>
                      <Button
                        as={RouterLink}
                        to={Url.OrgAdmin.Payments.activity(org)}
                        variant="outline"
                        colorScheme="primary"
                      >
                        Payments activity
                      </Button>
                      <WithUserPermissions permissions={["payments:create"]}>
                        <ConditionalTooltip
                          showTooltip={!paymentConfig.isPaymentConfigured}
                          label="Complete payment setup before creating a new payment."
                        >
                          <Button
                            variant="solid"
                            colorScheme="primary"
                            onClick={() => setIsCreateDrawerOpen(true)}
                            isDisabled={!paymentConfig.isPaymentConfigured}
                          >
                            Create new payment
                          </Button>
                        </ConditionalTooltip>
                      </WithUserPermissions>
                    </HStack>
                  }
                />
              </>
            }
          >
            {showEmptyState ? (
              <Empty
                onCreatePayment={() => setIsCreateDrawerOpen(true)}
                isPaymentConfigured={paymentConfig.isPaymentConfigured}
              />
            ) : (
              <Flex direction="column" gap={4}>
                <HStack>
                  <SearchInput
                    onSearch={handleActionSearch}
                    value={searchParams.get(SearchAndFilterTypes.Search) ?? ""}
                    onClear={handleClearSearch}
                    placeholder="Search by payment name, payment description, or payment amount"
                  />
                </HStack>

                <PaymentsList
                  payments={payments}
                  limit={pagination.limit}
                  offset={pagination.offset}
                  count={totalCount}
                  onFetchMore={setPagination}
                  onChangeSort={setOrderBy}
                  onClearSearch={handleClearSearch}
                  onEdit={(paymentId) => {
                    const payment = payments.find((p) => p.id === paymentId);
                    if (payment) {
                      setEditingPayment(payment);
                    }
                  }}
                  onDelete={handleDelete}
                  onRequestPayment={handleRequestPayment}
                  isLoading={templatesRD.isLoading()}
                  isPaymentConfigured={paymentConfig.isPaymentConfigured}
                />
              </Flex>
            )}

            <CreatePaymentDrawer
              organizationId={org.id}
              isOpen={isCreateDrawerOpen}
              onClose={() => setIsCreateDrawerOpen(false)}
            />

            <EditPaymentDrawer
              payment={editingPayment}
              isOpen={!!editingPayment}
              onClose={() => setEditingPayment(null)}
            />

            <RequestPaymentDrawer
              paymentTemplateId={requestPaymentForId}
              isOpen={!!requestPaymentForId}
              onClose={() => setRequestPaymentForId(null)}
              onSuccess={() => {
                queryClient.invalidateQueries({
                  queryKey: ["paymentTemplates"],
                });
                queryClient.invalidateQueries({
                  queryKey: ["payments"],
                });
              }}
            />

            {confirmationDialog}
          </AdminPage>
        );
      }}
    </RemoteDataView>
  );
};
